import axios from "axios";

// Backend base URL. If REACT_APP_BACKEND_URL is not set at build time we fall back to
// the same origin (frontend + backend behind one nginx), so requests never go to "undefined/api".
export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((cfg) => {
  let t = null;
  try { t = localStorage.getItem("sm_token"); } catch {}
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => {
    // If nginx (or any proxy) answers an /api call with the SPA's index.html, treat it as an
    // error instead of handing an HTML string to components (that used to crash -> blank page).
    const ct = String(r.headers?.["content-type"] || "");
    if (ct.includes("text/html") || (typeof r.data === "string" && r.data.trim().startsWith("<"))) {
      const err = new Error(`API returned HTML instead of JSON for ${r.config?.url}. Check the /api proxy in nginx.`);
      err.response = { status: 502, data: { detail: "Server is not reachable right now. Please try again." } };
      return Promise.reject(err);
    }
    return r;
  },
  (err) => Promise.reject(err)
);

/** Always returns an array (protects .map/.filter from bad responses). */
export const asArray = (x) => (Array.isArray(x) ? x : []);

/**
 * Resolve a media URL for <img>/<video>.
 * Files uploaded to our own server are stored as "/api/uploads/..." — prefix the backend host
 * when the frontend is served from a different domain. External URLs are returned unchanged.
 */
export function mediaUrl(u) {
  if (!u || typeof u !== "string") return u || "";
  if (u.startsWith("/api/uploads/")) return `${BACKEND_URL}${u}`;
  return u;
}

export const isUploadedFile = (u) => typeof u === "string" && u.includes("/api/uploads/");

const YT_RE = /(?:youtube\.com\/(?:embed\/|watch\?v=|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/;
/** Returns a YouTube embed URL for any YouTube link, or null if it's not YouTube. */
export function youtubeEmbed(u) {
  const m = typeof u === "string" ? u.match(YT_RE) : null;
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export const isVideoFile = (u) => typeof u === "string" && /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|$)/i.test(u);

/* ---------------- Uploads ----------------
 * Files are sent in 512 KB pieces to /media/upload-chunk. Each request stays far below the
 * default 1 MB nginx body limit, so photos and large videos upload even on servers whose
 * nginx was never tuned. Large photos are also resized in the browser before upload.
 */
const CHUNK = 512 * 1024;
let chunkUnsupported = false;
const randomId = () => Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, "0")).join("");

const isHeic = (f) => /image\/hei(c|f)/i.test(f.type) || /\.(heic|heif)$/i.test(f.name || "");
const baseName = (n) => String(n || "photo").replace(/\.[^.]+$/, "");

async function drawToJpeg(src, name, max = 2400) {
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
  const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.naturalWidth * scale); canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.86));
  if (!blob) throw new Error("encode failed");
  return new File([blob], `${baseName(name)}.jpg`, { type: "image/jpeg" });
}

/**
 * Get a photo ready for upload: iPhone/Samsung HEIC photos become JPEG, very large photos are
 * resized to 2400px, and anything the browser can show but the server can't store (e.g. BMP, TIFF
 * in Safari) is converted to JPEG.
 */
async function prepareImage(file) {
  try {
    const t = (file.type || "").toLowerCase();
    if (t.startsWith("video/")) return file;
    if (isHeic(file)) {
      // Safari can decode HEIC itself; other browsers use a converter loaded only when needed
      const url = URL.createObjectURL(file);
      try { return await drawToJpeg(url, file.name); } catch { /* fall through */ } finally { URL.revokeObjectURL(url); }
      const { default: heic2any } = await import(/* webpackChunkName: "heic" */ "heic2any");
      const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.86 });
      const blob = Array.isArray(out) ? out[0] : out;
      const jpg = new File([blob], `${baseName(file.name)}.jpg`, { type: "image/jpeg" });
      return jpg.size > 900 * 1024 ? await prepareImage(jpg) : jpg;
    }
    const supported = /^image\/(jpeg|jpg|png|webp|gif|avif)$/.test(t);
    if (supported && (t === "image/gif" || file.size < 900 * 1024)) return file;
    if (!t.startsWith("image/") && !/\.(jpe?g|png|webp|bmp|tiff?)$/i.test(file.name || "")) return file;
    const url = URL.createObjectURL(file);
    try {
      const jpg = await drawToJpeg(url, file.name);
      return supported && jpg.size >= file.size ? file : jpg;
    } finally { URL.revokeObjectURL(url); }
  } catch {
    return file;
  }
}

async function postChunk(fd, tries = 3) {
  for (let i = 0; ; i++) {
    try {
      const { data } = await api.post("/media/upload-chunk", fd, { timeout: 120000 });
      return data;
    } catch (e) {
      const st = e?.response?.status;
      if (i >= tries - 1 || (st && st < 500 && st !== 408 && st !== 429)) throw e;
      await new Promise((r) => setTimeout(r, 800 * (i + 1)));
    }
  }
}

async function postWhole(file, folder) {
  const fd = new FormData();
  fd.append("files", file, file.name);
  fd.append("folder", folder);
  const { data } = await api.post("/media/upload", fd, { timeout: 300000 });
  return Array.isArray(data) ? data[0] : data?.files?.[0] || data;
}

/** Plain-language reason for a failed upload. */
export function uploadErrorMessage(e) {
  const st = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (st === 413) return "This file is larger than the server accepts.";
  if (st === 422) return "The server rejected the upload. It may still be running the old version — ask the admin to update it.";
  if (st === 401) return "Your session expired — sign in again, then upload.";
  if (st === 404 || st === 405) return "The server hasn't been updated yet, so uploads aren't available. Ask the admin to update the backend.";
  if (st === 403) return "Your account isn't allowed to upload files.";
  if (!e?.response) return "Upload interrupted — check your internet connection and try again.";
  return "Upload failed. Please try again.";
}

/**
 * Upload files to the server's own storage.
 * @param {File[]} files
 * @param {"venues"|"blogs"|"testimonials"|"cities"|"misc"} folder
 * @param {(pct:number)=>void} [onProgress]
 * @returns {Promise<{url:string, kind:"image"|"video", name:string, size:number}[]>}
 */
export async function uploadMedia(files, folder = "misc", onProgress) {
  const list = [];
  for (const f of Array.from(files)) list.push(await prepareImage(f));
  const totalBytes = list.reduce((n, f) => n + f.size, 0) || 1;
  let sent = 0;
  const out = [];
  for (const file of list) {
    if (chunkUnsupported) {
      // older server without piece-by-piece uploads: send the whole file in one request
      const item = await postWhole(file, folder);
      if (item?.url) out.push(item);
      sent += file.size;
      if (onProgress) onProgress(Math.min(99, Math.round((sent * 100) / totalBytes)));
      continue;
    }
    const id = randomId();
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    let res = null;
    for (let i = 0; i < total; i++) {
      const piece = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
      const fd = new FormData();
      fd.append("chunk", piece, "blob");
      fd.append("upload_id", id);
      fd.append("index", String(i));
      fd.append("total", String(total));
      fd.append("filename", file.name);
      fd.append("folder", folder);
      try {
        res = await postChunk(fd);
      } catch (e) {
        const st = e?.response?.status;
        if (i === 0 && (st === 404 || st === 405)) { chunkUnsupported = true; break; }
        throw e;
      }
      sent += piece.size;
      if (onProgress) onProgress(Math.min(99, Math.round((sent * 100) / totalBytes)));
    }
    if (chunkUnsupported && !res) {
      // retry this same file through the single-request route
      const item = await postWhole(file, folder);
      if (item?.url) out.push(item);
      sent += file.size;
      continue;
    }
    if (res?.done) out.push(res);
  }
  if (onProgress) onProgress(100);
  return out;
}

const FIELD_LABEL = { email: "Email", password: "Password", name: "Name", phone: "Mobile number", staff_role: "Role", city: "City",
  category: "Category", address: "Address", description: "Description", amount: "Amount", received_by: "Received by", received_on: "Received on",
  payer_name: "Paid by", plan: "Plan", months: "Duration", title: "Title" };

/** Turn any API error body into one readable sentence (names the field for validation errors). */
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  const one = (e) => {
    if (!e || typeof e !== "object") return String(e ?? "");
    const loc = Array.isArray(e.loc) ? e.loc.filter((x) => x !== "body" && typeof x === "string") : [];
    const key = loc[loc.length - 1];
    const label = key ? FIELD_LABEL[key] || key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "";
    const msg = typeof e.msg === "string" ? e.msg : JSON.stringify(e);
    if (!label) return msg;
    if (/field required|missing/i.test(msg)) return `${label} is required`;
    if (/valid email|not a valid email/i.test(msg)) return `${label}: enter a valid email address`;
    return `${label}: ${msg.replace(/^Value error, /, "")}`;
  };
  if (Array.isArray(detail)) return detail.map(one).filter(Boolean).join(". ");
  if (typeof detail === "object") return one(detail);
  return String(detail);
}
